import React, { useState } from "react";
import {
  Box, Typography, TextField, Button,
  CircularProgress, Grid, Divider, useMediaQuery, useTheme,
  Tabs, Tab,
} from "@mui/material";
import QrCode2Icon     from "@mui/icons-material/QrCode2";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { toast }       from "react-toastify";
import { PaymentService } from "../../../services/PaymentService";
import { UPI_APPS, buildAppUrl } from "../../../constants/paymentMaps";
import UpiQr from "../UpiQr";
import { useNavigate } from "react-router-dom";

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#d1d5db" },
    "&:hover fieldset": { borderColor: "#15b86d" },
    "&.Mui-focused fieldset": { borderColor: "#15b86d", borderWidth: "2px" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#15b86d" },
};

// ── sessionStorage key helpers ─────────────────────────────────────
const ssKey  = (prefix, txnId) => `${prefix}_${txnId}`;

// ── QR Panel (shared by both dynamic-QR and static-QR tabs) ───────
// mopType: "QR" | "SQR"
const QrPanel = ({ txn, mopType, intentLocked, onPaymentInitiated, handlePaymentFailure }) => {
  const [qrState, setQrState]           = useState(() => {
    const s = sessionStorage.getItem(ssKey(`qrState_${mopType}`, txn.transactionId));
    return s === "ready" ? "ready" : "idle";
  });
  const [qrIntentLink, setQrIntentLink] = useState(() =>
    sessionStorage.getItem(ssKey(`qrLink_${mopType}`, txn.transactionId)) || ""
  );

  const handleShowQR = async () => {
    if (intentLocked) return;
    setQrState("loading");
    try {
      const res = await PaymentService.submitPayReq({
        orderId:         txn.transactionId,
        paymentType:     "UP",
        mopType,
        vpa: "", cardType: "", cardNumber: "",
        cardExpiryMonth: "", cardExpiryYear: "",
        cardSecurity: "", cardHolderName: "",
      });
      if (res.statusCode === 200) {
        const link = res.intentLink;
        setQrIntentLink(link);
        sessionStorage.setItem(ssKey(`qrLink_${mopType}`, txn.transactionId), link);
        setQrState("ready");
        sessionStorage.setItem(ssKey(`qrState_${mopType}`, txn.transactionId), "ready");
        onPaymentInitiated("qr");   // silent polling, QR stays visible
      } else {
        toast.error(res.message || "Failed to generate QR code");
        setQrState("idle");
      }
    } catch {
      toast.error("Failed to generate QR code");
      setQrState("idle");
      handlePaymentFailure("Failed to generate QR code");
    }
  };

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 230,
          border: "1px solid #e5e7eb",
          borderRadius: "14px",
          overflow: "hidden",
          background: "#fafbfc",
          opacity: intentLocked ? 0.4 : 1,
          transition: "opacity 0.3s ease",
          pointerEvents: intentLocked ? "none" : "auto",
        }}
      >
        <Box
          sx={{
            filter: qrState === "ready" ? "none" : "blur(10px)",
            transition: "filter 0.4s ease",
            pointerEvents: qrState === "ready" ? "auto" : "none",
            p: 2,
          }}
        >
          {qrState === "ready" && qrIntentLink ? (
            <UpiQr url={qrIntentLink} />
          ) : (
            <QrCode2Icon sx={{ fontSize: 200, color: "#d1d5db" }} />
          )}
        </Box>

        {qrState !== "ready" && (
          <Box
            sx={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 1,
            }}
          >
            {qrState === "loading" ? (
              <>
                <CircularProgress size={36} sx={{ color: "#15b86d" }} />
                <Typography variant="caption" color="#6b7280">Generating QR…</Typography>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleShowQR}
                disabled={intentLocked}
                startIcon={<QrCode2Icon />}
                sx={{
                  background: "linear-gradient(135deg, #15b86d 0%, #0ea5e9 100%)",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3, py: 1.25,
                  boxShadow: "0 4px 18px rgba(21,184,109,0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #13a360 0%, #0284c7 100%)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {mopType === "SQR" ? "Show Static QR" : "Show QR Code"}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {qrState === "ready" && (
        <Typography variant="caption" color="#6b7280" display="block" textAlign="center" mt={1}>
          {mopType === "SQR"
            ? "Scan this static QR with any UPI app · Waiting for payment…"
            : "Open any UPI app and scan this code · Waiting for payment…"}
        </Typography>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const UpiSection = ({ mops, txn, onPaymentInitiated }) => {
  const theme    = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const hasSQR     = mops.includes("SQR");
  const hasQR      = mops.includes("QR");
  const hasIntent  = mops.includes("INTENT");
  const hasCollect = mops.includes("COLLECT");

  // ── QR tab selection ────────────────────────────────────────────
  // 0 = Dynamic QR (QR),  1 = Static QR (SQR)
  // Persisted per-txn so switching payment types and coming back keeps it
  const [qrTab, setQrTab] = useState(() => {
    const stored = sessionStorage.getItem(ssKey("qrTab", txn.transactionId));
    return stored !== null ? Number(stored) : 0;
  });

  // Once the 2nd API is called (QR revealed OR intent clicked) the tabs lock permanently
  const [tabLocked, setTabLocked] = useState(() =>
    sessionStorage.getItem(ssKey("qrTabLocked", txn.transactionId)) === "true"
  );

  const handleTabChange = (_, newVal) => {
    if (tabLocked) return;
    setQrTab(newVal);
    sessionStorage.setItem(ssKey("qrTab", txn.transactionId), String(newVal));
  };

  // ── Intent (app tap) state ──────────────────────────────────────
  const [intentLoadingApp, setIntentLoadingApp] = useState(null);

  // ── Collect state ───────────────────────────────────────────────
  const [vpa, setVpa]                   = useState("");
  const [vpaName, setVpaName]           = useState("");
  const [collectState, setCollectState] = useState("idle");

  const intentLocked = intentLoadingApp !== null;

  // ── Shared helpers ──────────────────────────────────────────────
  const handlePaymentFailure = (message) => {
    navigate("/response-page", {
      replace: true,
      state: {
        response: {
          status: "FAILED",
          responseMessage: message,
          txnId:   txn.transactionId,
          orderId: txn.orderId,
          amount:  txn.amount,
        },
      },
    });
  };

  // Called by QrPanel once the QR is shown → lock tabs
  const handlePaymentInitiated = (mode) => {
    setTabLocked(true);
    sessionStorage.setItem(ssKey("qrTabLocked", txn.transactionId), "true");
    onPaymentInitiated(mode);
  };

  // ── Intent app handler ──────────────────────────────────────────
  const handleIntentAppClick = async (app) => {
    if (intentLocked) return;
    setIntentLoadingApp(app.name);

    // Lock tabs immediately when any app is tapped
    setTabLocked(true);
    sessionStorage.setItem(ssKey("qrTabLocked", txn.transactionId), "true");

    // Check if a QR intent link was already generated (reuse it)
    const existingLink =
      sessionStorage.getItem(ssKey("qrLink_QR", txn.transactionId)) ||
      sessionStorage.getItem(ssKey("qrLink_SQR", txn.transactionId));

    if (existingLink) {
      const appUrl = buildAppUrl(existingLink, app.scheme);
      sessionStorage.setItem("shouldPollPayment", "true");
      sessionStorage.setItem("pollingOrderId", txn.transactionId);
      onPaymentInitiated("intent");
      setTimeout(() => { window.location.href = appUrl; }, 300);
      return;
    }

    try {
      const res = await PaymentService.submitPayReq({
        orderId:     txn.transactionId,
        paymentType: "UP",
        mopType:     "INTENT",
        vpa: "", cardType: "", cardNumber: "",
        cardExpiryMonth: "", cardExpiryYear: "",
        cardSecurity: "", cardHolderName: "",
      });
      if (res.statusCode === 200) {
        const appUrl = buildAppUrl(res.intentLink, app.scheme);
        sessionStorage.setItem("shouldPollPayment", "true");
        sessionStorage.setItem("pollingOrderId", txn.transactionId);
        onPaymentInitiated("intent");
        setTimeout(() => { window.location.href = appUrl; }, 300);
      } else {
        toast.error(res.message || "Failed to launch UPI app");
        setIntentLoadingApp(null);
      }
    } catch {
      toast.error("Failed to launch UPI app");
      setIntentLoadingApp(null);
    }
  };

  // ── Collect handlers ────────────────────────────────────────────
  const handleVerifyVPA = async () => {
    if (!vpa.trim()) { toast.warning("Please enter a UPI ID"); return; }
    setCollectState("verifying");
    try {
      const res = await PaymentService.verifyVPA(vpa.trim());
      if (res.statusCode === 200 && res.data?.status === "VALID") {
        setVpaName(res.data.customer_name || "Verified");
        setCollectState("verified");
        toast.success(`Verified: ${res.data.customer_name || "Valid UPI ID"}`);
      } else {
        toast.error("Invalid UPI ID — please try again");
        setCollectState("idle");
      }
    } catch {
      toast.error("VPA verification failed");
      setCollectState("idle");
    }
  };

  const handleCollectPay = async () => {
    setCollectState("sending");
    try {
      const res = await PaymentService.submitPayReq({
        orderId:     txn.transactionId,
        paymentType: "UP",
        mopType:     "COLLECT",
        vpa:         vpa.trim(),
        cardType: "", cardNumber: "",
        cardExpiryMonth: "", cardExpiryYear: "",
        cardSecurity: "", cardHolderName: "",
      });
      if (res.statusCode === 200 && res.isRequested) {
        setCollectState("pending");
        onPaymentInitiated("collect");
      } else {
        toast.error(res.message || "Failed to send collect request");
        setCollectState("verified");
      }
    } catch {
      toast.error("Failed to send payment request");
      setCollectState("verified");
      handlePaymentFailure("Failed to send payment request");
    }
  };

  // ── Decide which QR mop the current tab shows ───────────────────
  // If both QR and SQR exist → tab bar; otherwise render the one that exists
  const showTabBar = hasSQR && hasQR;
  const activeQrMop = showTabBar ? (qrTab === 0 ? "QR" : "SQR") : (hasSQR ? "SQR" : "QR");
  const isStaticTab = activeQrMop === "SQR";

  const showQrBlock    = hasSQR || hasQR;
  const showIntentBlock = hasIntent && isMobile && !isStaticTab;
  const showCollectBlock = hasCollect && !isStaticTab;

  // ── Render ──────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── QR Block ──────────────────────────────────────────── */}
      {showQrBlock && (
        <Box mb={(showIntentBlock || showCollectBlock) ? 3 : 0}>

          {/* Tab bar — only rendered when both QR and SQR are available */}
          {showTabBar && (
            <Tabs
              value={qrTab}
              onChange={handleTabChange}
              sx={{
                mb: 1.5,
                minHeight: 36,
                "& .MuiTabs-indicator": { backgroundColor: "#15b86d", height: 3 },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  minHeight: 36,
                  py: 0.5,
                  color: "#6b7280",
                  "&.Mui-selected": { color: "#15b86d" },
                  // Disabled styling once locked
                  "&.Mui-disabled": { opacity: 0.45, cursor: "not-allowed", pointerEvents: "auto" },
                },
              }}
            >
              <Tab label="QR Code"    id="qr-tab-0"  disabled={tabLocked} />
              <Tab label="Static QR"  id="qr-tab-1"  disabled={tabLocked} />
            </Tabs>
          )}

          {/* Heading */}
          <Typography variant="subtitle1" fontWeight={600} color="#1f2937" mb={1.5}>
            {isStaticTab ? "Scan Static QR Code" : "Scan QR Code"}
          </Typography>

          <QrPanel
            key={activeQrMop}               // remount when tab switches so state is fresh
            txn={txn}
            mopType={activeQrMop}
            intentLocked={intentLocked}
            onPaymentInitiated={handlePaymentInitiated}
            handlePaymentFailure={handlePaymentFailure}
          />
        </Box>
      )}

      {/* ── Divider between QR and Intent / Collect ─────────── */}
      {showQrBlock && (showIntentBlock || showCollectBlock) && (
        <Divider sx={{ my: 2.5 }}>
          <Typography variant="caption" color="#9ca3af" sx={{ px: 1 }}>or</Typography>
        </Divider>
      )}

      {/* ── UPI App Intent (mobile only, hidden on Static QR tab) ─ */}
      {showIntentBlock && (
        <Box mb={showCollectBlock ? 3 : 0}>
          <Typography variant="subtitle1" fontWeight={600} color="#1f2937" mb={1.5}>
            Pay via UPI App
          </Typography>
          <Grid container spacing={1.5} justifyContent="center">
            {UPI_APPS.map((app) => (
              <Grid item xs={4} sm={2.4} key={app.name}>
                <Box
                  onClick={() => handleIntentAppClick(app)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: intentLocked ? "not-allowed" : "pointer",
                    p: 1.5,
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    bgcolor: "white",
                    transition: "all 0.25s ease",
                    opacity: intentLocked && intentLoadingApp !== app.name ? 0.35 : 1,
                    pointerEvents: intentLocked && intentLoadingApp !== app.name ? "none" : "auto",
                    "&:hover": intentLocked ? {} : {
                      borderColor: "#15b86d",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 14px rgba(21,184,109,0.18)",
                    },
                  }}
                >
                  {intentLoadingApp === app.name ? (
                    <CircularProgress size={38} sx={{ color: "#15b86d", mb: 0.5 }} />
                  ) : app.logo ? (
                    <Box
                      component="img"
                      src={app.logo}
                      alt={app.name}
                      sx={{ width: 40, height: 40, objectFit: "contain", mb: 0.5 }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 40, height: 40, borderRadius: "50%",
                        background: "linear-gradient(135deg, #15b86d, #0ea5e9)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="white" fontWeight={700} fontSize="0.6rem">
                        UPI
                      </Typography>
                    </Box>
                  )}
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    color="#475569"
                    textAlign="center"
                    sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {app.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Typography variant="caption" color="#9ca3af" display="block" textAlign="center" mt={1.5}>
            Tap an app to pay instantly
          </Typography>
        </Box>
      )}

      {showIntentBlock && showCollectBlock && (
        <Divider sx={{ my: 2.5 }}>
          <Typography variant="caption" color="#9ca3af" sx={{ px: 1 }}>or</Typography>
        </Divider>
      )}

      {/* ── UPI ID / Collect (hidden on Static QR tab) ─────────── */}
      {showCollectBlock && (
        <Box sx={{ opacity: intentLocked ? 0.4 : 1, transition: "opacity 0.3s", pointerEvents: intentLocked ? "none" : "auto" }}>
          <Typography variant="subtitle1" fontWeight={600} color="#1f2937" mb={1.5}>
            Enter UPI ID
          </Typography>
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              label="UPI ID (e.g. name@upi)"
              value={vpa}
              onChange={(e) => {
                setVpa(e.target.value);
                if (collectState === "verified") { setCollectState("idle"); setVpaName(""); }
              }}
              fullWidth
              size="small"
              disabled={["verifying", "sending", "pending"].includes(collectState)}
              sx={INPUT_SX}
              helperText={vpaName ? `✓ ${vpaName}` : undefined}
              FormHelperTextProps={{ style: { color: "#15b86d", fontWeight: 600 } }}
            />
            {collectState === "verified" ? (
              <Button
                variant="contained"
                onClick={handleCollectPay}
                disabled={collectState === "sending"}
                sx={{
                  minWidth: 80, height: 40, mt: "1px", flexShrink: 0,
                  background: "#15b86d", borderRadius: "8px",
                  textTransform: "none", fontWeight: 600,
                  "&:hover": { background: "#13a360" },
                }}
              >
                {collectState === "sending" ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Pay"}
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={handleVerifyVPA}
                disabled={collectState === "verifying" || !vpa.trim()}
                startIcon={collectState === "verifying" ? <CircularProgress size={14} /> : <VerifiedUserIcon />}
                sx={{
                  minWidth: 88, height: 40, mt: "1px", flexShrink: 0,
                  borderColor: "#15b86d", color: "#15b86d", borderRadius: "8px",
                  textTransform: "none", fontWeight: 600,
                  "&:hover": { borderColor: "#13a360", color: "#13a360" },
                  "&:disabled": { borderColor: "#e5e7eb", color: "#9ca3af" },
                }}
              >
                {collectState === "verifying" ? "…" : "Verify"}
              </Button>
            )}
          </Box>
          <Typography variant="caption" color="#9ca3af" display="block" mt={0.5}>
            Verify your UPI ID before proceeding to pay
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default UpiSection;
